import React, { useState, useEffect } from 'react';
import { adminAPI } from '../services/api';
import Header from '../components/Header.jsx';

export default function AdminCertificates() {
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCertificate, setEditingCertificate] = useState(null);
  const [formData, setFormData] = useState({
    certificate_number: '',
    holder_name: '',
    degree: '',
    date_issued: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchCertificates();
  }, []);

  const fetchCertificates = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getCertificates();
      setCertificates(response.data.certificates);
    } catch (error) {
      console.error('Error fetching certificates:', error);
      setError('Failed to fetch certificates');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      if (editingCertificate) {
        await adminAPI.updateCertificate(editingCertificate.certificate_id, formData);
        setSuccess('Certificate updated successfully');
      } else {
        await adminAPI.createCertificate(formData);
        setSuccess('Certificate created successfully');
      }
      
      fetchCertificates();
      closeModal();
    } catch (error) {
      console.error('Error saving certificate:', error);
      setError(error.response?.data?.error || 'Failed to save certificate');
    }
  };

  const handleEdit = (certificate) => {
    setEditingCertificate(certificate);
    setFormData({
      certificate_number: certificate.certificate_number,
      holder_name: certificate.holder_name,
      degree: certificate.degree,
      date_issued: certificate.date_issued.split('T')[0]
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this certificate?')) {
      return;
    }

    try {
      await adminAPI.deleteCertificate(id);
      setSuccess('Certificate deleted successfully');
      fetchCertificates();
    } catch (error) {
      console.error('Error deleting certificate:', error);
      setError('Failed to delete certificate');
    }
  };

  const openModal = () => {
    setEditingCertificate(null);
    setFormData({
      certificate_number: '',
      holder_name: '',
      degree: '',
      date_issued: ''
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingCertificate(null);
    setError('');
  };

  const generateCertificateNumber = () => {
    const year = new Date().getFullYear();
    const lastCert = certificates.length > 0 ? certificates[0] : null;
    let nextNumber = 1;
    
    if (lastCert) {
      const match = lastCert.certificate_number.match(/\d+$/);
      if (match) {
        nextNumber = parseInt(match[0]) + 1;
      }
    }
    
    const certNumber = `RJ-${year}-0509${String(nextNumber).padStart(4, '0')}`;
    setFormData(prev => ({ ...prev, certificate_number: certNumber }));
  };

  return (
    <>
      <Header />
      <div className="section" style={{ padding: '30px' }}>
        <div className="admin-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
          <div>
            <h1 style={{ color: 'var(--maroon)', fontFamily: "'Playfair Display',serif", margin: 0 }}>
              📜 Certificate Management
            </h1>
            <p style={{ color: 'var(--muted)', margin: '8px 0 0 0' }}>
              Manage and verify educational certificates
            </p>
          </div>
          <button className="book" onClick={openModal}>
            + Add New Certificate
          </button>
        </div>

        {error && <div className="alert alert-error" style={{ backgroundColor: '#fee', color: '#c33', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>{error}</div>}
        {success && <div className="alert alert-success" style={{ backgroundColor: '#efe', color: '#3c3', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>{success}</div>}

        <div className="card" style={{ padding: '20px', marginBottom: '30px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-around', textAlign: 'center' }}>
            <div>
              <h3 style={{ color: 'var(--muted)', fontSize: '14px', margin: '0 0 8px' }}>Total Certificates</h3>
              <p style={{ fontSize: '32px', fontWeight: 'bold', color: 'var(--maroon)', margin: 0 }}>{certificates.length}</p>
            </div>
          </div>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '50px', color: 'var(--muted)' }}>Loading certificates...</div>
        ) : (
          <div className="card" style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f5f5f5', borderBottom: '2px solid #ddd' }}>
                  <th style={{ padding: '15px', textAlign: 'left', color: 'var(--maroon)' }}>Sr.No</th>
                  <th style={{ padding: '15px', textAlign: 'left', color: 'var(--maroon)' }}>Certificate Number</th>
                  <th style={{ padding: '15px', textAlign: 'left', color: 'var(--maroon)' }}>Holder Name</th>
                  <th style={{ padding: '15px', textAlign: 'left', color: 'var(--maroon)' }}>Degree/Course</th>
                  <th style={{ padding: '15px', textAlign: 'left', color: 'var(--maroon)' }}>Date Issued</th>
                  <th style={{ padding: '15px', textAlign: 'left', color: 'var(--maroon)' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
              {certificates.length === 0 ? (
                <tr>
                  <td colSpan="6" style={{ textAlign: 'center', padding: '40px', color: 'var(--muted)' }}>
                    No certificates found. Add your first certificate!
                  </td>
                </tr>
              ) : (
                certificates.map((cert, index) => (
                  <tr key={cert.certificate_id} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '15px' }}>{index + 1}</td>
                    <td style={{ padding: '15px', fontFamily: 'monospace', fontWeight: 'bold', color: 'var(--maroon)' }}>
                      {cert.certificate_number}
                    </td>
                    <td style={{ padding: '15px' }}>{cert.holder_name}</td>
                    <td style={{ padding: '15px' }}>{cert.degree}</td>
                    <td style={{ padding: '15px' }}>{new Date(cert.date_issued).toLocaleDateString('en-GB')}</td>
                    <td style={{ padding: '15px' }}>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button 
                          className="book"
                          onClick={() => handleEdit(cert)}
                          style={{ padding: '8px 16px', fontSize: '14px', backgroundColor: '#FF9800' }}
                        >
                          ✏️ Edit
                        </button>
                        <button 
                          className="book"
                          onClick={() => handleDelete(cert.certificate_id)}
                          style={{ padding: '8px 16px', fontSize: '14px', backgroundColor: '#F44336' }}
                        >
                          🗑️ Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        )}

      {isModalOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }} onClick={closeModal}>
          <div className="card" style={{
            maxWidth: '600px',
            width: '90%',
            maxHeight: '90vh',
            overflowY: 'auto',
            padding: '30px'
          }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ color: 'var(--maroon)', margin: 0 }}>
                {editingCertificate ? 'Edit Certificate' : 'Add New Certificate'}
              </h2>
              <button onClick={closeModal} style={{
                background: 'none',
                border: 'none',
                fontSize: '32px',
                cursor: 'pointer',
                color: 'var(--muted)',
                lineHeight: 1
              }}>×</button>
            </div>

            <form onSubmit={handleSubmit}>
              {error && <div style={{ backgroundColor: '#fee', color: '#c33', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>{error}</div>}

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#333' }}>
                  Certificate Number *
                </label>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <input
                    type="text"
                    name="certificate_number"
                    value={formData.certificate_number}
                    onChange={handleInputChange}
                    placeholder="RJ-2025-05090001"
                    required
                    style={{
                      flex: 1,
                      padding: '10px 15px',
                      border: '2px solid #ddd',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  />
                  {!editingCertificate && (
                    <button 
                      type="button"
                      className="book"
                      onClick={generateCertificateNumber}
                      style={{ whiteSpace: 'nowrap', padding: '10px 20px' }}
                    >
                      🔢 Auto Generate
                    </button>
                  )}
                </div>
                <small style={{ color: '#666', fontSize: '12px' }}>Format: RJ-YYYY-0509XXXX</small>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#333' }}>
                  Holder Name *
                </label>
                <input
                  type="text"
                  name="holder_name"
                  value={formData.holder_name}
                  onChange={handleInputChange}
                  placeholder="MR. John Doe"
                  required
                  style={{
                    width: '100%',
                    padding: '10px 15px',
                    border: '2px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#333' }}>
                  Degree/Course *
                </label>
                <input
                  type="text"
                  name="degree"
                  value={formData.degree}
                  onChange={handleInputChange}
                  placeholder="Astrology Course"
                  required
                  style={{
                    width: '100%',
                    padding: '10px 15px',
                    border: '2px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#333' }}>
                  Date Issued *
                </label>
                <input
                  type="date"
                  name="date_issued"
                  value={formData.date_issued}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: '100%',
                    padding: '10px 15px',
                    border: '2px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: '15px', marginTop: '30px' }}>
                <button 
                  type="button" 
                  className="book" 
                  onClick={closeModal}
                  style={{ flex: 1, backgroundColor: '#9e9e9e' }}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="book"
                  style={{ flex: 1 }}
                >
                  {editingCertificate ? 'Update Certificate' : 'Create Certificate'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      </div>
    </>
  );
}
